// Lightweight client-side field validators for the auth + account forms.
// Each returns a human-readable error string, or null when the value passes.
//
// Intentionally lenient: the server is the source of truth (credential checks,
// uniqueness, deliverability). These exist only to replace the browser's native
// constraint-validation popups ("Please fill out this field.") with inline,
// on-brand error text rendered by <Field>.
//
// i18n (SM-283): default messages resolve at CALL time via i18next.t under the
// auth namespace — module-level lib code, so a lookup at import time would
// freeze whatever language was current at boot. Callers may still pass an
// explicit message to override the default (e.g. reset-password's own copy).
import i18next from "@/lib/i18n";

// Pragmatic "looks like an email" check — exactly one @, a dot in the domain,
// no whitespace. Deliberately not RFC-5322-exhaustive; the confirmation email
// is what actually proves the address is real.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Backend minimum (client-service email provider rejects shorter). Keep this
// in sync with the server so the inline hint matches the real rule.
export const PASSWORD_MIN_LENGTH = 8;

export function validateEmail(value: string): string | null {
  const v = value.trim();
  if (!v) return i18next.t("auth:validation.emailRequired");
  if (!EMAIL_RE.test(v)) return i18next.t("auth:validation.emailInvalid");
  return null;
}

export function validatePassword(
  value: string,
  opts: { min?: number; emptyMessage?: string } = {},
): string | null {
  const { min = 0, emptyMessage } = opts;
  if (!value) return emptyMessage ?? i18next.t("auth:validation.passwordRequired");
  if (min && value.length < min) return i18next.t("auth:validation.passwordMin", { count: min });
  return null;
}

export function validateMatch(
  value: string,
  other: string,
  message?: string,
): string | null {
  return value === other ? null : (message ?? i18next.t("auth:validation.mismatch"));
}
